import dayjs from 'dayjs';
import { print } from 'graphql';
import gql from 'graphql-tag';
import type { MigrationStrategies, RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { captureInSentryWithDetails } from '@/utilities/captureInSentryWithDetails';
import { ENABLE_DEBUGGER } from '@/utilities/constants';

import { BaseEntity } from '../../models/BaseEntity';
import { generatePullQuery, PullQueryContext } from '../queryBuilders/generatePullQuery';
import { generatePushQuery } from '../rxdbUtilityFunctions';
import { defaultDoc90Day } from '../Shared/defaultDoc';

export interface GroundControlOptionValue {
  readonly optionId: string;
  readonly valueId: string | null;
  readonly value: string | null;
  readonly units: string | null;
  readonly attributeId: string | null;
  readonly attributeValueId: string | null;
  readonly attributeValue: string | null;
  readonly attributeIdUnits: string | null;
}

export interface GroundControlSet extends BaseEntity {
  readonly type: string;
  readonly quantity: number;
  readonly optionValues: GroundControlOptionValue[];
  readonly groundHazardId: string;
}

export type GroundControlSetDocument = RxDocument<GroundControlSet>;
export type GroundControlSetCollection = RxCollection<GroundControlSet> | null;

export const groundControlSetSchema: RxJsonSchema<GroundControlSet> = {
  type: 'object',
  version: 1,
  description: 'describes a Ground Control Set object',
  primaryKey: 'id',
  properties: {
    id: { type: 'string', maxLength: 36 },
    type: { type: 'string' },
    quantity: { type: ['number', 'null'] },
    optionValues: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          optionId: { type: 'string' },
          valueId: { type: ['string', 'null'] },
          value: { type: ['string', 'null'] },
          units: { type: ['string', 'null'] },
          attributeId: { type: ['string', 'null'] },
          attributeValueId: { type: ['string', 'null'] },
          attributeValue: { type: ['string', 'null'] },
          attributeIdUnits: { type: ['string', 'null'] },
        },
        required: ['optionId'],
      },
    },
    updatedAt: { type: 'number' },
    isDeleted: { type: 'boolean' },
    version: { type: 'number' },
    groundHazardId: { type: 'string' },
  },
};

export const groundControlSetMigrationStrategies: MigrationStrategies = {
  1: (oldDoc: GroundControlSetDocument) => oldDoc,
};

const groundControlSetFeedQuery = print(gql`
  query groundControlSetFeed($siteId: ID!, $lastUpdateAt: Float!, $limit: Int!) {
    groundControlSetFeed(siteId: $siteId, lastUpdateAt: $lastUpdateAt, limit: $limit) {
      id
      isDeleted
      optionValues
      quantity
      type
      updatedAt
      version
    }
  }
`);

export const groundControlSetsPullQueryBuilder = generatePullQuery(
  groundControlSetFeedQuery,
  PullQueryContext.Site,
  defaultDoc90Day,
);

const createGroundControlSetMutation = print(gql`
  mutation CreateGroundControlSet($groundControlSet: CreateGroundControlSetInput) {
    createGroundControlSet(input: $groundControlSet)
  }
`);

type CreateGroundControlSetInput = {
  id: string;
  createdOn: string | null;
  quantity: number;
  optionValues: string | null;
  type: string;
  groundHazardId: string;
};

const mapGroundControlSetDocToInput = (
  doc: GroundControlSetDocument,
): CreateGroundControlSetInput => {
  return {
    id: doc.id,
    type: doc.type,
    quantity: doc.quantity,
    optionValues: JSON.stringify(doc.optionValues),
    createdOn: dayjs(doc.updatedAt).toISOString(),
    groundHazardId: doc.groundHazardId,
  };
};

export const groundControlSetPushQueryBuilder = generatePushQuery(
  createGroundControlSetMutation,
  (doc: GroundControlSetDocument) => ({
    groundControlSet: mapGroundControlSetDocToInput(doc),
  }),
);

export const parseGroundControlOptionValues = (
  optionValues: GroundControlSet['optionValues'] | string,
): GroundControlSet['optionValues'] => {
  let parsedOptionValues: unknown = optionValues;

  while (typeof parsedOptionValues === 'string') {
    parsedOptionValues = JSON.parse(parsedOptionValues);
  }

  return Array.isArray(parsedOptionValues)
    ? (parsedOptionValues as GroundControlSet['optionValues'])
    : [];
};

export const groundControlSetsPullModifier = (doc: GroundControlSetDocument) => {
  return {
    ...doc,
    optionValues: parseGroundControlOptionValues(doc.optionValues),
  };
};

export const groundControlSetsPushModifier = (doc: GroundControlSetDocument) => {
  if (Boolean(doc.groundHazardId) === true) {
    return doc;
  } else {
    if (ENABLE_DEBUGGER) debugger;
    captureInSentryWithDetails(
      new Error('groundControlSetsPushModifier: groundHazardId is missing.'),
      { documentDetails: JSON.stringify(doc), rxdbResyncBug: true },
    );
    return null;
  }
};
